import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { ProductSpeciesClient, ProductSpeciesRequest, ProductSpeciesResponse } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface ProductSpecies extends ProductSpeciesResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    productSpeciesList: ProductSpecies[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    productSpeciesList: [],
};

const productSpeciesSlice = createSlice({
    name: 'productSpecies',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getProductSpeciesListSuccess: (state, action: PayloadAction<ProductSpecies[]>) => {
            state.productSpeciesList = action.payload;
            state.isLoading = false;
        },
        addNewProductSpeciesSuccess: (state, action: PayloadAction<ProductSpecies>) => {
            const newItem = action.payload;
            state.productSpeciesList = [newItem, ...state.productSpeciesList];
            state.isLoading = false;
        },
        updateProductSpeciesSuccess: (state, action: PayloadAction<ProductSpecies>) => {
            const updatedItem = action.payload;
            const index = state.productSpeciesList.findIndex(item => item.productSpeciesId === updatedItem.productSpeciesId);
            state.productSpeciesList = [...state.productSpeciesList.slice(0, index), updatedItem, ...state.productSpeciesList.slice(index + 1)];
            state.isLoading = false;
        },
    },
});

// Reducer
export default productSpeciesSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getProductSpeciesListSuccess,
    addNewProductSpeciesSuccess,
    updateProductSpeciesSuccess,
} = productSpeciesSlice.actions;

// for useSelector
export const selectProductSpecies = (state: RootState) => state.productSpecies as State;

// ----------------------------------------------------------------------
export function getAllProductSpecies(ignoreCache: boolean = false) {
    return async (dispatch, getState) => {
        try {
            const { productSpeciesList } = selectProductSpecies(getState());
            if (ignoreCache || productSpeciesList.length === 0) {
                dispatch(productSpeciesSlice.actions.startLoading());
                const client = new ProductSpeciesClient(undefined, axios);
                const response = await client.getAllProductSpecies(false);
                dispatch(productSpeciesSlice.actions.getProductSpeciesListSuccess(response));
            }
        } catch (error) {
            dispatch(productSpeciesSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createProductSpecies(productSpecies: ProductSpecies, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productSpeciesSlice.actions.startLoading());
        try {
            const { productSpeciesId, ...payload } = productSpecies;
            const request = payload as ProductSpeciesRequest;
            const client = new ProductSpeciesClient(undefined, axios);
            const response = await client.createProductSpecies(request);
            dispatch(productSpeciesSlice.actions.addNewProductSpeciesSuccess(response));
            if (showNotification) {
                toast.success(`${response.productSpeciesCode} created successfully!`);
            }
        } catch (error) {
            dispatch(productSpeciesSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateProductSpecies(productSpecies: ProductSpecies, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productSpeciesSlice.actions.startLoading());
        try {
            const { productSpeciesId, ...payload } = productSpecies;
            const request = payload as ProductSpeciesRequest;
            const client = new ProductSpeciesClient(undefined, axios);
            const response = await client.editProductSpecies(productSpeciesId, request);
            dispatch(productSpeciesSlice.actions.updateProductSpeciesSuccess(response));
            if (showNotification) {
                toast.success(`${response.productSpeciesCode} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
            }
        } catch (error) {
            dispatch(productSpeciesSlice.actions.hasError(true));
            handleError(error);
        }
    };
}